import type { RelatedEntityType } from "@dune/types";
import type { EntityPanelConfig, PanelComponents } from "../types/panel.types";

const panelRegistry = new Map<RelatedEntityType, EntityPanelConfig>();

const isDevelopment = import.meta.env.DEV;

export const registerEntityPanels = (config: EntityPanelConfig) => {
	panelRegistry.set(config.entityType, config);
};

export const unregisterEntityPanels = (entityType: RelatedEntityType) => {
	panelRegistry.delete(entityType);
};

export const getEntityPanelComponents = (
	entityType: RelatedEntityType,
): PanelComponents | null => {
	const config = panelRegistry.get(entityType);
	return config?.components || null;
};

export const getEntityPanelComponent = <K extends keyof PanelComponents>(
	entityType: RelatedEntityType,
	componentKey: K,
): PanelComponents[K] | null => {
	const components = getEntityPanelComponents(entityType);

	if (!components) {
		if (isDevelopment) {
			console.warn(`[PanelRegistry] No components found for ${entityType}`);
		}
		return null;
	}

	const component = components[componentKey];

	// Only warn for required components (EmailComposer, TaskDetails)
	// EmailPreview and DocumentPreview are optional and use fallbacks
	const isOptionalComponent =
		componentKey === "EmailPreview" || componentKey === "DocumentPreview";

	if (!component && isDevelopment && !isOptionalComponent) {
		console.warn(
			`[PanelRegistry] Component '${String(componentKey)}' not found for ${entityType}`,
			{
				availableComponents: Object.keys(components),
			},
		);
	}

	return component || null;
};

export const isEntityTypeRegistered = (
	entityType: RelatedEntityType,
): boolean => {
	return panelRegistry.has(entityType);
};

export const getRegisteredEntityTypes = (): RelatedEntityType[] => {
	return Array.from(panelRegistry.keys());
};

export const debugPanelRegistry = () => {
	if (isDevelopment) {
		console.group("[PanelRegistry] Debug Info");
		console.log("Registered entity types:", getRegisteredEntityTypes());
		console.log("Registry contents:", Object.fromEntries(panelRegistry));
		console.groupEnd();
	}
};
